import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

import DateTimeDisplay from '../../components/DateTimeDisplay'
import DetailPageTemplate from '../../templates/DetailPageTemplate'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const AnalysisPage = () => {
  const [analysis, setAnalysis] = useState(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const { uuid } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await fetchWithAuth(`analyses/${uuid}`)
        setAnalysis(data)
      } catch (error) {
        console.error('Error fetching analysis:', error)
      }
    }

    fetchData()
  }, [uuid])

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleDelete = async () => {
    try {
      await fetchWithAuth(`analyses/${uuid}`, { method: 'DELETE' })
      navigate('/analyses')
    } catch (error) {
      console.error('Error deleting analysis:', error)
    }
  }

  if (!analysis) {
    return <DetailPageTemplate isLoading={true} />
  }

  const detailsSection = {
    title: 'Analysis Details',
    content: (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 500, flexShrink: 0 }}
              >
                Name
              </Typography>
              <Typography>{analysis.name}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 500, flexShrink: 0 }}
              >
                Slug
              </Typography>
              <code variant="muted">{analysis.slug}</code>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 500, flexShrink: 0 }}
              >
                Description
              </Typography>
              <Typography>{analysis.description}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 500, flexShrink: 0 }}
              >
                Input Data types
              </Typography>
              <Grid spacing={1} container>
                {analysis.input_datatype_ids.map((option) => (
                  <Chip label={option.name} key={option.slug} />
                ))}
              </Grid>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 500, flexShrink: 0 }}
              >
                Needs GPU
              </Typography>
              {analysis.gpu_required ? 'true' : 'false'}
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 500, flexShrink: 0 }}
              >
                Created At
              </Typography>
              <DateTimeDisplay date={new Date(analysis.createdAt)} />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    ),
  }

  const parametersSection = {
    title: 'Parameters',
    content: (
      <Card variant="outlined">
        <CardContent>
          {!analysis.parameters ||
          Object.keys(analysis.parameters).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              No parameters defined
            </Typography>
          ) : (
            <Stack spacing={2}>
              {Object.entries(analysis.parameters).map(([key, value]) => (
                <Box key={key} display="flex" alignItems="flex-start" gap={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ width: 500, flexShrink: 0 }}
                  >
                    {key}
                  </Typography>
                  <Typography>{value.toString()}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    ),
  }

  const defaultParametersSection = {
    title: 'Default Parameters',
    content: (
      <Card variant="outlined">
        <CardContent>
          {!analysis.default_parameters ||
          Object.keys(analysis.default_parameters).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              No default parameters defined
            </Typography>
          ) : (
            <Stack spacing={2}>
              {Object.entries(analysis.default_parameters).map(
                ([key, value]) => (
                  <Box key={key} display="flex" alignItems="flex-start" gap={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ width: 500, flexShrink: 0 }}
                    >
                      {key}
                    </Typography>
                    <FormControl fullWidth>
                      {typeof value === 'boolean' ? (
                        <Typography>{value.toString()}</Typography>
                      ) : typeof value === 'number' ? (
                        <Typography>{value}</Typography>
                      ) : value === null ? (
                        <Typography></Typography>
                      ) : (
                        <Typography>{value}</Typography>
                      )}
                    </FormControl>
                  </Box>
                ),
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    ),
  }

  const timeseriesParametersSection = {
    title: 'Timeseries Parameters',
    content: (
      <Card variant="outlined">
        <CardContent>
          {!analysis.timeseries_parameters ||
          Object.keys(analysis.timeseries_parameters).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              No timeseries parameters defined
            </Typography>
          ) : (
            <Stack spacing={2}>
              {Object.entries(analysis.timeseries_parameters).map(
                ([key, value]) => (
                  <Box key={key} display="flex" alignItems="flex-start" gap={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ width: 500, flexShrink: 0 }}
                    >
                      {key}
                    </Typography>
                    <FormControl fullWidth>
                      {typeof value === 'boolean' ? (
                        <Typography>{value.toString()}</Typography>
                      ) : typeof value === 'number' ? (
                        <Typography>{value}</Typography>
                      ) : value === null ? (
                        <Typography></Typography>
                      ) : (
                        <Typography>{value}</Typography>
                      )}
                    </FormControl>
                  </Box>
                ),
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    ),
  }

  const histogramParametersSection = {
    title: 'Histogram Parameters',
    content: (
      <Card variant="outlined">
        <CardContent>
          {!analysis.histogram_parameters ||
          Object.keys(analysis.histogram_parameters).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              No histogram parameters defined
            </Typography>
          ) : (
            <Stack spacing={2}>
              {Object.entries(analysis.histogram_parameters).map(
                ([key, value]) => (
                  <Box key={key} display="flex" alignItems="flex-start" gap={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ width: 500, flexShrink: 0 }}
                    >
                      {key}
                    </Typography>
                    <Typography>{value.toString()}</Typography>
                  </Box>
                ),
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    ),
  }
  return (
    <>
      <DetailPageTemplate
        title={analysis.name}
        breadcrumbs={['analyses', analysis.name ?? 'analyses']}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              component={Link}
              to={`/analyses/${uuid}/edit`}
              variant="contained"
              color="primary"
            >
              Edit Analysis
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
            >
              Delete Analysis
            </Button>
          </Stack>
        }
        sections={[
          detailsSection,
          parametersSection,
          defaultParametersSection,
          timeseriesParametersSection,
          histogramParametersSection,
        ]}
      />

      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Analysis</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {analysis.name}? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AnalysisPage
